/**
 * ============================================================================
 * Emission Drawer Component
 * ============================================================================
 * 
 * Drawer latéral pour ajouter une émission:
 * - Formulaire de saisie (valeur + description)
 * - Détails du facteur sélectionné (avec explication multilingue)
 * - Calcul automatique du CO₂
 * 
 * ============================================================================
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { getCategoryDesign } from '@/lib/calculator-design';
import { Loader2, Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface EmissionFactor {
  fe_key: string;
  // Generic fields (fallback)
  category: string;
  subcategory?: string;
  process_name?: string;
  unit?: string;
  factor_value?: number;
  scope?: string;
  source_name?: string;
  // Multilingual - category
  category_en?: string | null;
  category_fr?: string | null;
  category_ar?: string | null;
  // Multilingual - subcategory
  subcategory_en?: string | null;
  subcategory_fr?: string | null;
  subcategory_ar?: string | null;
  // Multilingual - process name
  process_name_en?: string | null;
  process_name_fr?: string | null;
  process_name_ar?: string | null;
  // Multilingual - unit
  unit_en?: string | null;
  unit_fr?: string | null;
  unit_ar?: string | null;
  // Multilingual - scope
  scope_en?: string | null;
  scope_fr?: string | null;
  scope_ar?: string | null;
  // Multilingual - factor unit
  factor_unit?: string | null;
  factor_unit_en?: string | null;
  factor_unit_fr?: string | null;
  factor_unit_ar?: string | null;
  // Descriptions
  explanation_en?: string | null;
  explanation_fr?: string | null;
  explanation_ar?: string | null;
  notes?: string | null;
}

export interface EmissionDrawerProps {
  open: boolean;
  factor: EmissionFactor | null;
  onClose: () => void;
  onSubmit: (data: { value: number; description?: string }) => void;
  isSubmitting?: boolean;
  initialValue?: number;
  initialDescription?: string;
}

export function EmissionDrawer({
  open,
  factor,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialValue,
  initialDescription,
}: EmissionDrawerProps) {
  const { i18n } = useTranslation();
  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');

  // Initialize fields with existing data when drawer opens
  useEffect(() => {
    if (open && factor) {
      setValue(initialValue ? String(initialValue) : '');
      setDescription(initialDescription || '');
    }
  }, [open, factor, initialValue, initialDescription]);

  const handleSubmit = () => {
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      return;
    }
    onSubmit({
      value: numValue,
      description: description.trim() || undefined,
    });
  };

  const handleClose = () => {
    setValue('');
    setDescription('');
    onClose();
  };

  if (!factor) return null;

  // Determine current language
  const lang = i18n.language?.split('-')[0] || 'fr';

  // Helper: pick localized value with fallback chain
  const loc = (en: string | null | undefined, fr: string | null | undefined, ar: string | null | undefined, fallback?: string | null) => {
    if (lang === 'ar') return ar || en || fr || fallback || '';
    if (lang === 'fr') return fr || en || fallback || '';
    return en || fr || fallback || '';
  };

  // Localized fields
  const localCategory    = loc(factor.category_en, factor.category_fr, factor.category_ar, factor.category);
  const localSubcategory = loc(factor.subcategory_en, factor.subcategory_fr, factor.subcategory_ar, factor.subcategory);
  const localProcessName = loc(factor.process_name_en, factor.process_name_fr, factor.process_name_ar, factor.process_name);
  const localUnit        = loc(factor.unit_en, factor.unit_fr, factor.unit_ar, factor.unit);
  const localScope       = loc(factor.scope_en, factor.scope_fr, factor.scope_ar, factor.scope);
  const localFactorUnit  = loc(factor.factor_unit_en, factor.factor_unit_fr, factor.factor_unit_ar, factor.factor_unit || (factor.unit ? `tCO₂e/${localUnit}` : null));
  const explanation      = loc(factor.explanation_en, factor.explanation_fr, factor.explanation_ar);

  const design = getCategoryDesign(factor.category);
  const Icon = design.icon;
  const estimatedCO2 = factor.factor_value && value
    ? (parseFloat(value) * factor.factor_value).toFixed(4)
    : null;

  // Labels localized
  const labels = {
    factorDetails:   lang === 'fr' ? 'Détails du facteur d\'émission' : lang === 'ar' ? 'تفاصيل عامل الانبعاثات' : 'Emission Factor Details',
    subcategoryLbl:  lang === 'fr' ? 'Sous-catégorie :' : lang === 'ar' ? 'الفئة الفرعية:' : 'Subcategory:',
    scopeLbl:        lang === 'fr' ? 'Portée :' : lang === 'ar' ? 'النطاق:' : 'Scope:',
    unitLbl:         lang === 'fr' ? 'Unité :' : lang === 'ar' ? 'الوحدة:' : 'Unit:',
    factorLbl:       lang === 'fr' ? 'Facteur :' : lang === 'ar' ? 'العامل:' : 'Factor:',
    sourceLbl:       lang === 'fr' ? 'Source :' : lang === 'ar' ? 'المصدر:' : 'Source:',
    howToEnter:      lang === 'fr' ? 'Comment saisir cette valeur ?' : lang === 'ar' ? 'كيفية إدخال هذه القيمة؟' : 'How to enter this value?',
    activityValue:   lang === 'fr' ? 'Valeur d\'activité' : lang === 'ar' ? 'قيمة النشاط' : 'Activity Value',
    descriptionLbl:  lang === 'fr' ? 'Description' : lang === 'ar' ? 'الوصف' : 'Description',
    optional:        lang === 'fr' ? '(optionnel)' : lang === 'ar' ? '(اختياري)' : '(optional)',
    placeholder:     lang === 'fr' ? 'Ajouter une note...' : lang === 'ar' ? 'أضف ملاحظة...' : 'Add a note about this emission...',
    estimatedCO2:    lang === 'fr' ? 'CO₂ estimé :' : lang === 'ar' ? 'ثاني أكسيد الكربون المقدر:' : 'Estimated CO₂:',
    cancel:          lang === 'fr' ? 'Annuler' : lang === 'ar' ? 'إلغاء' : 'Cancel',
    addEmission:     lang === 'fr' ? 'Ajouter l\'émission' : lang === 'ar' ? 'إضافة الانبعاث' : 'Add Emission',
    adding:          lang === 'fr' ? 'Ajout en cours...' : lang === 'ar' ? 'جارٍ الإضافة...' : 'Adding...',
    noteLbl:         lang === 'fr' ? 'Note : ' : lang === 'ar' ? 'ملاحظة: ' : 'Note: ',
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-2 rounded-lg ${design.bgColor}`}>
              <Icon className={`h-5 w-5 ${design.color}`} />
            </div>
            <div className="flex-1">
              <SheetTitle className="text-left">
                {localProcessName || localSubcategory || 'Add Emission'}
              </SheetTitle>
              <SheetDescription className="text-left">
                {localCategory}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Factor Details */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-semibold text-sm">{labels.factorDetails}</h4>
            <div className="space-y-2 text-sm">
              {localSubcategory && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.subcategoryLbl}</span>
                  <span className="font-medium">{localSubcategory}</span>
                </div>
              )}
              {localScope && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.scopeLbl}</span>
                  <Badge variant="secondary">{localScope}</Badge>
                </div>
              )}
              {localUnit && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.unitLbl}</span>
                  <span className="font-medium">{localUnit}</span>
                </div>
              )}
              {factor.factor_value !== undefined && factor.factor_value !== null && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.factorLbl}</span>
                  <span className="font-medium">{factor.factor_value} {localFactorUnit || `tCO₂e/${localUnit || 'unit'}`}</span>
                </div>
              )}
              {factor.source_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{labels.sourceLbl}</span>
                  <span className="font-medium text-xs">{factor.source_name}</span>
                </div>
              )}
            </div>
          </div>

          {/* Explanation / Description */}
          {explanation && (
            <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-1">
                    {labels.howToEnter}
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 leading-relaxed">
                    {explanation}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {factor.notes && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-xs text-amber-700 dark:text-amber-400">
                <span className="font-semibold">{labels.noteLbl}</span>{factor.notes}
              </p>
            </div>
          )}

          {/* Input Form */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="value">
                {labels.activityValue}{localUnit && ` (${localUnit})`}
                <span className="text-destructive ml-1">*</span>
              </Label>
              <Input
                id="value"
                type="number"
                step="any"
                min="0"
                placeholder={localUnit ? `${lang === 'fr' ? 'Entrez la valeur en' : lang === 'ar' ? 'أدخل القيمة بـ' : 'Enter value in'} ${localUnit}` : 'Enter value'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {labels.descriptionLbl} <span className="text-muted-foreground text-xs">{labels.optional}</span>
              </Label>
              <Textarea
                id="description"
                placeholder={labels.placeholder}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
                rows={3}
              />
            </div>
          </div>

          {/* Estimated CO2 */}
          {estimatedCO2 && (
            <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{labels.estimatedCO2}</span>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">
                    {estimatedCO2}
                  </div>
                  <div className="text-xs text-muted-foreground">tCO₂e</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {labels.cancel}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!value || parseFloat(value) < 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {labels.adding}
              </>
            ) : (
              labels.addEmission
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
